import React, { Suspense, lazy, useEffect, useRef } from 'react';
import Navbar from './Components/Navbar/Navbar';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import ContactUs from './Components/ContactUs/ContactUs';
import Footer from './Components/Footer/Footer';
import ScrollToTop from './Components/ScrollToTop/ScrollToTop';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import Fab from '@mui/material/Fab';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ReactGA from 'react-ga4';
import CookieConsent from './Components/CookieConsent/CookieConsent';
import { useConsent } from './Components/CookieConsent/ConsentContext';

const Inicio = lazy(() => import('./pages/Inicio'));
const SantAndreu = lazy(() => import('./pages/SantAndreu'));
const Martorell = lazy(() => import('./pages/Martorell'));
const AvisoLegal = lazy(() => import('./pages/Legal/AvisoLegal'));
const PoliticaPrivacidad = lazy(() => import('./pages/Legal/PoliticaPrivacidad'));
const PoliticaCookies = lazy(() => import('./pages/Legal/PoliticaCookies'));

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

const deleteAnalyticsCookies = () => {
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (name === '_ga' || name.startsWith('_ga_')) {
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${window.location.hostname}; SameSite=Lax`;
    }
  });
};

const RouteTracker = () => {
  const location = useLocation();
  const { preferences } = useConsent();
  const initializedRef = useRef(false);
  const lastPageRef = useRef(null);

  useEffect(() => {
    if (!GA_ID) return;

    window[`ga-disable-${GA_ID}`] = !preferences.analytics;

    if (!preferences.analytics) {
      deleteAnalyticsCookies();
      lastPageRef.current = null;
      return;
    }

    if (!initializedRef.current) {
      ReactGA.initialize(GA_ID);
      initializedRef.current = true;
    }

    const page = location.pathname + location.search;
    if (lastPageRef.current !== page) {
      ReactGA.send({ hitType: 'pageview', page });
      lastPageRef.current = page;
    }
  }, [location, preferences.analytics]);

  return null;
};

const AppContent = () => {
  const location = useLocation();
  const legalRoutes = [
    '/aviso-legal',
    '/politica-privacidad',
    '/politica-de-privacidad',
    '/politica-cookies',
    '/politica-de-cookies',
  ];
  const isLegalPage = legalRoutes.includes(location.pathname.toLowerCase());

  return (
    <>
      <ScrollToTop />
      <RouteTracker />
      <Navbar theme={theme} />
      <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
        <Routes>
          <Route path="/" element={<Inicio />} />
          <Route path="/SantAndreu" element={<SantAndreu />} />
          <Route path="/santandreu" element={<SantAndreu />} />
          <Route path="/santAndreu" element={<SantAndreu />} />
          <Route path="/Martorell" element={<Martorell />} />
          <Route path="/martorell" element={<Martorell />} />
          <Route path="/aviso-legal" element={<AvisoLegal />} />
          <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/politica-de-privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/politica-cookies" element={<PoliticaCookies />} />
          <Route path="/politica-de-cookies" element={<PoliticaCookies />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {!isLegalPage && (
        <>
          <div
            style={{
              position: 'fixed',
              bottom: '16px',
              right: '16px',
              zIndex: 1000,
            }}
          >
            <Fab
              color="primary"
              href="https://wa.me/+34692871659"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contactar por WhatsApp"
            >
              <WhatsAppIcon />
            </Fab>
          </div>
          <ContactUs />
        </>
      )}
      <Footer />
      <CookieConsent />
    </>
  );
};

const App = () => (
  <ThemeProvider theme={theme}>
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  </ThemeProvider>
);

export default App;
